import { useEffect, useRef, useState } from 'react'
import { COLORS } from '../lib/theme'
import { useTranslation } from '../i18n'
import { Icon } from './Icon'

export interface JoinHuntModalProps {
  /**
   * Join with the entered code. Resolve `'joined'` for a real join or
   * `'viewed'` for a read-only look at a hunt that already ended. Reject to
   * show a failure inline: an error whose `code` is one of
   * {@link JoinHuntFailureCode} is shown in the UI language, anything else as
   * the generic "that code didn't work".
   */
  onJoin: (code: string) => Promise<'joined' | 'viewed'>
  onClose: () => void
  /** Validate the code format before enabling Join (e.g. NNNLLL). Optional. */
  validateFormat?: (code: string) => boolean
}

/**
 * Accessible "Join hunt" popup: enter a hunt code to join. Matches the in-app
 * modal pattern (scrim + centered card), traps Escape / click-outside, and
 * surfaces success/failure inline (no global toast infra yet). The host owns
 * the actual join (callable) via onJoin.
 */
/**
 * Why a join failed, as the host reports it on the rejected error's `code`.
 * The host carries the reason rather than a rendered sentence, so the message
 * follows the UI language: `expired` (the code is valid but will never work
 * again), `ambiguous` (it matches two live hunts) or `failed` (anything else).
 */
export type JoinHuntFailureCode = 'failed' | 'expired' | 'ambiguous'

export function JoinHuntModal({ onJoin, onClose, validateFormat }: JoinHuntModalProps) {
  const { t } = useTranslation('hunts')
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [outcome, setOutcome] = useState<'joined' | 'viewed' | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const trimmed = code.trim()
  const formatOk = trimmed.length > 0 && (!validateFormat || validateFormat(trimmed))

  const submit = async () => {
    if (!formatOk || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await onJoin(trimmed)
      setOutcome(result)
      setDone(true)
    } catch (err) {
      const code = (err as { code?: unknown } | null)?.code
      setError(t(code === 'expired' || code === 'ambiguous' ? `joinModal.errors.${code}` : 'joinModal.errors.failed'))
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('joinModal.title')}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: 24,
          maxWidth: 380,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        {done ? (
          <>
            <p style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: COLORS.onSurface, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="check" size={18} color={COLORS.primary} />
              {outcome === 'viewed' ? t('joinModal.viewed') : t('joinModal.joined')}
            </p>
            {outcome !== 'viewed' && (
              <p style={{ margin: '0 0 20px', fontSize: 13, color: COLORS.outline }}>
                {t('joinModal.joinedBody')}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="wh-btn wh-btn-primary" onClick={onClose}>{t('joinModal.done')}</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{t('joinModal.title')}</p>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: COLORS.outline }}>
              {t('joinModal.intro')}
            </p>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 600, color: COLORS.outline }}>
              {t('joinModal.codeLabel')}
              <input
                ref={inputRef}
                className="wh-input"
                type="text"
                value={code}
                placeholder={t('joinModal.placeholder')}
                autoCapitalize="characters"
                aria-invalid={!!error}
                onChange={(e) => { setCode(e.target.value); setError(null) }}
                onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
                style={{ textTransform: 'uppercase', letterSpacing: 2, fontFamily: 'ui-monospace, Menlo, monospace' }}
              />
            </label>
            {error && (
              <div role="alert" style={{ marginTop: 10, fontSize: 12, color: COLORS.error }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="wh-btn wh-btn-text" onClick={onClose} disabled={submitting}>{t('joinModal.cancel')}</button>
              <button className="wh-btn wh-btn-primary" onClick={submit} disabled={!formatOk || submitting}>
                {submitting ? t('joinModal.joining') : t('joinModal.submit')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
